import { useRef, useEffect } from 'react';
import * as THREE from 'three';
import isWebGLAvailable from '../lib/isWebGLAvailable';

/**
 * Real 3D Earth Globe using raw Three.js (bypasses R3F reconciler issues).
 * Uses NASA Blue Marble texture for real continents.
 *
 * v6.1.3 Phase G4 Part A — Falls back to a static CSS-gradient sphere
 * when WebGL is unavailable (headless browsers, low-spec devices, VMs
 * with no GPU passthrough).  Pre-fix: `new THREE.WebGLRenderer(...)`
 * threw on construction and bubbled into the top-level ErrorBoundary,
 * which then showed the fatal "Something went wrong" screen for the
 * entire app — even though only the decorative globe was broken.
 *
 * Props:
 *   size: pixel diameter (default 56)
 *   spinning: whether currently pressed/spinning
 *   spinSpeed: 0-10 speed multiplier
 *   brightness: 0.0-2.0 ambient light intensity (default 0.7, tweak to taste)
 *   baseSpeed: idle rotation speed when not pressed (default 0, set >0 for splash)
 */
export default function Globe3D({ size = 56, spinning = false, spinSpeed = 0, brightness = 0.7, baseSpeed = 0, opaque = false, onReady }) {
  const containerRef = useRef(null);
  const internalsRef = useRef(null);
  const spinningRef = useRef(spinning);
  const spinSpeedRef = useRef(spinSpeed);
  const opaqueRef = useRef(opaque);
  const brightnessRef = useRef(brightness);
  opaqueRef.current = opaque;
  const baseSpeedRef = useRef(baseSpeed);
  // 2026-07 — fires once the NASA texture is on the sphere (or on any
  // fallback path) so callers can reveal globe + halo + masks together.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Keep refs in sync with props
  spinningRef.current = spinning;
  spinSpeedRef.current = spinSpeed;
  brightnessRef.current = brightness;
  baseSpeedRef.current = baseSpeed;

  // v6.1.3 Phase G4 Part A — WebGL probe.  We evaluate once at mount
  // time; the answer cannot change at runtime within the same document.
  const webglOk = isWebGLAvailable();

  useEffect(() => {
    if (!webglOk) { onReadyRef.current?.(); return; }  // static fallback path — don't try Three.js
    const container = containerRef.current;
    if (!container) return;

    let mounted = true;
    let currentSpeed = 0;

    // --- Scene Setup ---
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = 2.5;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: !opaque, antialias: true });
    } catch (_err) {
      // Probe said yes but instantiation failed (driver crash / context
      // limit exhaustion).  Bail to static fallback without bubbling.
      onReadyRef.current?.();
      return;
    }
    renderer.setSize(size, size);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(opaque ? 0x020208 : 0x000000, opaque ? 1 : 0);
    container.appendChild(renderer.domElement);

    // --- Lights (brightness adjustable via prop) ---
    const ambientLight = new THREE.AmbientLight(0xffffff, brightness);
    scene.add(ambientLight);
    const sun = new THREE.DirectionalLight(0xffffff, Math.max(0.8, brightness));
    sun.position.set(5, 3, 3);
    scene.add(sun);
    const fill = new THREE.DirectionalLight(0x6688cc, 0.3);
    fill.position.set(-3, -1, -2);
    scene.add(fill);

    // --- Earth: Start with plain dark blue, load NASA texture async ---
    const earthGeo = new THREE.SphereGeometry(1, 64, 64);
    const earthMat = new THREE.MeshPhongMaterial({
      color: 0x1a5276,  // Ocean blue placeholder while NASA loads
      shininess: 30,
      specular: new THREE.Color(0x444444),
    });
    const earth = new THREE.Mesh(earthGeo, earthMat);
    scene.add(earth);

    // Load NASA Blue Marble texture (public domain) — self-hosted since the
    // 2026-07 UX audit (Wikimedia CDN was a blocking 3rd-party boot dependency).
    const textureLoader = new THREE.TextureLoader();
    const earthTexUrl = '/blue_marble_1280.jpg';

    textureLoader.load(
      earthTexUrl,
      (nasaTex) => {
        nasaTex.colorSpace = THREE.SRGBColorSpace;
        earthMat.map = nasaTex;
        earthMat.color.set(0xffffff); // Reset tint so texture shows true colors
        earthMat.needsUpdate = true;
        onReadyRef.current?.();
      },
      undefined,
      () => { onReadyRef.current?.(); /* Failed to load — stays as plain blue sphere */ }
    );

    // Atmosphere
    const atmoGeo = new THREE.SphereGeometry(1.05, 32, 32);
    const atmoMat = new THREE.MeshBasicMaterial({ color: 0x88bbee, transparent: true, opacity: 0.08, side: THREE.BackSide });
    const atmo = new THREE.Mesh(atmoGeo, atmoMat);
    scene.add(atmo);

    // --- Animate ---
    let lastT = performance.now();
    const loop = () => {
      if (!mounted) return;
      const now = performance.now();
      const dt = Math.min((now - lastT) / 1000, 0.1);
      lastT = now;

      const target = spinningRef.current ? spinSpeedRef.current : baseSpeedRef.current;
      const rate = spinningRef.current ? 4 : 2;
      currentSpeed += (target - currentSpeed) * rate * dt;
      if (!spinningRef.current && baseSpeedRef.current === 0 && Math.abs(currentSpeed) < 0.01) currentSpeed = 0;

      earth.rotation.y += currentSpeed * dt * 1.5;
      atmoMat.opacity = 0.08 + currentSpeed * 0.015;

      renderer.render(scene, camera);
      requestAnimationFrame(loop);
    };
    renderer.render(scene, camera); // initial still frame
    requestAnimationFrame(loop);

    internalsRef.current = { renderer, scene, camera, earth, atmo, earthGeo, earthMat, atmoGeo, atmoMat };

    return () => {
      mounted = false;
      try { container.removeChild(renderer.domElement); } catch(e) {}
      renderer.dispose(); earthGeo.dispose(); earthMat.dispose(); atmoGeo.dispose(); atmoMat.dispose();
      if (earthMat.map) earthMat.map.dispose();
    };
  }, [size, brightness, opaque, webglOk]);

  // v6.1.3 Phase G4 Part A — Static fallback sphere when WebGL is
  // unavailable.  Pure CSS radial-gradient + box-shadow so it still
  // looks like a planet at small sizes (the brand-visible use case is
  // a 56-100px badge).  Mirrors the dominant NASA-blue palette.
  if (!webglOk) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          background:
            'radial-gradient(circle at 35% 30%, #4a7ba6 0%, #1a5276 35%, #0c2942 75%, #04101f 100%)',
          boxShadow:
            'inset -' + Math.max(2, size / 20) + 'px -' + Math.max(2, size / 20) + 'px ' + Math.max(4, size / 10) + 'px rgba(0,0,0,0.4), 0 0 ' + Math.max(2, size / 16) + 'px rgba(100,150,200,0.25)',
          overflow: 'hidden',
        }}
        aria-label="Earth globe (static fallback — WebGL unavailable)"
        role="img"
        data-testid="globe-3d-fallback"
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}
      data-testid="globe-3d"
    />
  );
}
